import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { Form, Formik } from 'formik';
import * as yup from 'yup';

import { isDefined } from 'common';

import { useUploadFileContext } from './fileUploadContext';
import { OrganizationControl } from '../../components/Control/OrganizationControl';
import { TextFieldControl } from '../../components/Control/TextField';
import { DialogCloseButton } from '../../components/DialogCloseButton';
import { OrganizationLabel } from '../../components/Label/Organization';
import { yupUuid } from '../../utils/form';

interface FormValues {
  organizationId: string;
  userDefinedName: string;
}

export function KnowledgeBaseUploadFileDialog() {
  const {
    organizationsAreVisible,
    uploadFile,
    isPending,
    file,
    setFile,
    canUpdateOrganizationId,
    presetOrganizationId,
  } = useUploadFileContext();

  return (
    <Dialog
      open={isDefined(file)}
      onClose={() => {
        setFile(undefined);
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogCloseButton onClose={() => setFile(undefined)} />
      <DialogTitle>Upload File</DialogTitle>
      <Formik<FormValues>
        initialValues={{
          organizationId: presetOrganizationId ?? '',
          userDefinedName: file?.name ?? '',
        }}
        enableReinitialize
        validationSchema={yup.object({
          organizationId: yupUuid().required('Organization is required'),
          userDefinedName: yup.string().required('File name is required'),
        })}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            await uploadFile(values);
            setFile(undefined);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, isValid, resetForm }) => (
          <Form>
            <DialogContent>
              <Stack gap={2}>
                {!canUpdateOrganizationId ? (
                  <OrganizationLabel
                    organizationIds={values.organizationId ? [values.organizationId] : []}
                  />
                ) : (
                  organizationsAreVisible && (
                    <OrganizationControl
                      name="organizationId"
                      label="Organization"
                      isMultiple={false}
                    />
                  )
                )}
                <TextFieldControl name="userDefinedName" label="File name" required />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                variant="text"
                onClick={() => {
                  setFile(undefined);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={isPending || !isValid}>
                Add file
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
